import { useEffect, useMemo } from 'react';
import { useIsOffline } from 'components/common/withIsOffline';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import { projectClient } from 'shared/domain/project/projectClient';
import type { ProjectModel } from 'shared/domain/project/types/model';
import { useEntityCollectionInitializer } from '../../entityCollection';
import { useProjectsStore } from '../store';
import type { ProjectsActions } from '../types';

/**
 * Loads the projects and keeps them current; read again on project change, as
 * before. Once a project was selected, the list is downloaded again whenever
 * the page is online (projects may have been added or archived meanwhile).
 */
export function useProjectsInitializer(): void {
  const isOffline = useIsOffline();
  useEntityCollectionInitializer(useProjectsStore.use.actions());

  useEffect(() => {
    if (!isOffline && LocalStorageManager.wasProjectSelected()) {
      projectClient.sync();
    }
  }, [isOffline]);
}

/** Projects sorted by name, with their loading and failure state. */
export function useProjects(): { items: ProjectModel[]; loading: boolean; syncFailed: boolean } {
  const { items, loading } = useProjectsStore.use.data();
  const syncFailed = useProjectsStore.use.syncFailed();
  return useMemo(() => ({ items, loading, syncFailed }), [items, loading, syncFailed]);
}

/** Projects actions (stable references). */
export function useProjectsActions(): ProjectsActions {
  return useProjectsStore.use.actions();
}
